// Home.jsx
import React, { useContext, useState, useEffect } from "react";
import ContentBox from "../components/ContentBox";
import HabboImage from "../components/HabboImage";
import StampCard from "../components/StampCard";
import PollBox from "../components/PollBox";
import ProfileSearch from "../components/ProfileSearch";
import ArticleCarousel from "../components/ArticleCarousel";
import ZonePicker from "../components/events/ZonePicker";
import EventCard from "../components/events/EventCard";
import { offsetLabel, readStoredZone, writeStoredZone } from "../components/events/eventsPublic";
import LatestArticles from "../components/LatestArticles";
import { Link } from "react-router-dom";
import { FontScaleContext } from "../App";

function Home({ userData }) {
    const fontScale = useContext(FontScaleContext);
    const [randomHabbos, setRandomHabbos] = useState([]);
    const [loadingRandom, setLoadingRandom] = useState(true);
    const [spotlightArticles, setSpotlightArticles] = useState([]);
    const [loadingSpotlight, setLoadingSpotlight] = useState(true);
    const [stamps, setStamps] = useState({ totalStamps: 0, completedCards: 0 });
    const [loadingStamps, setLoadingStamps] = useState(true);
    const [latestGuides, setLatestGuides] = useState([]);

    useEffect(() => {
        const fetchRandomHabbos = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}habbo/random`
                );
                const data = await response.json();

                if (response.status === 429) {
                    console.error("Rate limited");
                    return;
                }

                if (data.success) {
                    setRandomHabbos(data.data);
                }
            } catch (error) {
                console.error("Failed to fetch random habbos:", error);
            } finally {
                setLoadingRandom(false);
            }
        };

        fetchRandomHabbos();
    }, []);

    useEffect(() => {
        const fetchSpotlight = async () => {
            try {
                const apiUrl = process.env.REACT_APP_API_URL;

                const [articlesRes, observerRes] = await Promise.all([
                    fetch(`${apiUrl}articles/spotlight`),
                    fetch(`${apiUrl}observer/issues/spotlight`),
                ]);

                const articlesData = await articlesRes.json();
                const observerData = await observerRes.json();

                const articles = articlesData.success ? articlesData.articles : [];

                const observerItems = observerData.success
                    ? observerData.issues.map((issue) => ({
                        id: `observer-${issue.id}`,
                        title: `Issue #${issue.issueNumber} of the Origins Observer has landed!`,
                        publicDate: issue.publicDate,
                        headerImage: issue.coverImageUrl || null,
                        overview: "What's the latest gossip around the hotel? Inside scoop, a spotlight? Find out inside!",
                        spotlight: true,
                        spotlightColorId: issue.spotlightColourId || 3,
                        categoryName: 'Origins Observer',
                        href: `/observer?issue=${issue.issueNumber}`,
                    }))
                    : [];

                setSpotlightArticles([...articles, ...observerItems]);
            } catch (error) {
                console.error("Failed to fetch spotlight content:", error);
            } finally {
                setLoadingSpotlight(false);
            }
        };

        fetchSpotlight();
    }, []);

    useEffect(() => {
        const fetchLatestGuides = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}articles/guides/latest`);
                const data = await response.json();
                if (data.success) {
                    setLatestGuides(data.articles);
                }
            } catch (error) {
                console.error('Failed to fetch latest guides:', error);
            }
        };

        fetchLatestGuides();
    }, []);

    useEffect(() => {
        const fetchStamps = async () => {
            try {
                if (!userData?.userId) {
                    setLoadingStamps(false);
                    return;
                }

                const apiUrl = process.env.REACT_APP_API_URL;
                const response = await fetch(`${apiUrl}stamps/my-stamps`, {
                    credentials: 'include'
                });

                const data = await response.json();

                if (data.success) {
                    setStamps({
                        totalStamps: data.totalStamps || 0,
                        completedCards: data.completedCards || 0
                    });
                }
            } catch (error) {
                console.error('Error fetching stamps:', error);
            } finally {
                setLoadingStamps(false);
            }
        };

        fetchStamps();
    }, [userData?.userId]);

    const [latestUpdates, setLatestUpdates] = useState([]);

    useEffect(() => {
        (async () => {
            const res = await fetch(`${process.env.REACT_APP_API_URL}values/latest`);
            const data = await res.json();
            setLatestUpdates((data?.updates || []).slice(0, 6));
        })();
    }, []);

    const [events, setEvents] = useState([]);
    const [loadingEvents, setLoadingEvents] = useState(true);
    const [eventZone, setEventZone] = useState(() => readStoredZone());

    const changeEventZone = (zone) => {
        setEventZone(zone);
        writeStoredZone(zone);
    };

    useEffect(() => {
        const fetchUpcomingEvents = async () => {
            try {
                const apiUrl = process.env.REACT_APP_API_URL;
                const response = await fetch(`${apiUrl}events/upcoming`);
                const data = await response.json();

                if (data.success) {
                    setEvents(data.events);
                }
            } catch (error) {
                console.error('Error fetching upcoming events:', error);
            } finally {
                setLoadingEvents(false);
            }
        };

        fetchUpcomingEvents();
    }, []);

    function formatRelativeTime(timestamp) {
        const diff = Date.now() - new Date(timestamp).getTime();
        const seconds = Math.floor(diff / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        if (days > 0) return `Updated ${days} day${days > 1 ? "s" : ""} ago`;
        if (hours > 0) return `Updated ${hours} hour${hours > 1 ? "s" : ""} ago`;
        if (minutes > 0) return `Updated ${minutes} minute${minutes > 1 ? "s" : ""} ago`;
        return "Updated just now";
    }

    const carouselArticles = spotlightArticles;

    return (
        <div className="w-full min-h-screen p-4 lg2:p-6 overflow-hidden">
            <div className="grid grid-cols-1 gap-4 lg2:gap-6 lg2:grid-cols-[minmax(0,1fr)_360px]">
                <div className="min-w-0">
                    <ArticleCarousel
                        articles={carouselArticles}
                        backgroundTile="/img/grey_tile.png"
                        intervalMs={4500}
                    />

                    <ContentBox theme="default" header="Welcome to Frankly Origins!" icon="img/icons/icon_195.png">
                        <div className="flex flex-col sm:flex-row items-start gap-4">
                            <p className="text-gray-700">
                                Hey! Welcome to Frankly Origins! We're a fansite focused on Habbo Origins, the nostalgic revival of the classic Habbo Hotel. Frankly is your go-to destination for the latest news, updates, and community content about Habbo Origins. Whether you're a long-time Habbo veteran or a curious newcomer, we've got something for everyone!
                            </p>

                            <img src="/img/homepage_welcome.png" alt="Welcome!" className="flex-shrink-0 self-center sm:self-start" style={{ width: 185, height: 112, imageRendering: "pixelated" }}  />
                        </div>
                    </ContentBox>

                    {!loadingEvents && events.length > 0 && (
                        <ContentBox
                            theme="default"
                            tint="#E8871E"
                            icon="img/icons/icon_202.png"
                            header="Upcoming Events!"
                            headerRight={<ZonePicker zone={eventZone} onChange={changeEventZone} />}
                        >
                            <>
                                <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                                    <span className="font-['Jersey_10']">Next event:</span>
                                    <span className="text-[11px] uppercase tracking-wide text-gray-500">
                                        Shown in {eventZone.replace(/_/g, ' ')} ({offsetLabel(eventZone)})
                                    </span>
                                </div>
                                <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 cursor-default">
                                    {events.map((event, index) => (
                                        <EventCard
                                            key={event.id}
                                            event={event}
                                            zone={eventZone}
                                            muted={index > 0}
                                            showCalendar={false}
                                            className={index === 2 ? "lg:hidden xl:flex" : ""}
                                        />
                                    ))}
                                </div>

                                <div className="w-full text-right block underline font-['Jersey_10'] uppercase tracking-wide text-[14px]">
                                    <Link to="/events" className="">See More...</Link>
                                </div>
                            </>
                        </ContentBox>
                    )}

                    <ContentBox
                        theme="default"
                        tint="lightskyblue"
                        icon="img/icechair.png"
                        header="Latest Furni Values"
                    >
                        <div className="grid gap-2 grid-cols-1 xs:grid-cols-2 md:grid-cols-3 xl:grid-cols-6">
                            {latestUpdates.map((item) => (
                                <Link
                                    key={item.id}
                                    to={`/furniture/${item.name
                                        .toLowerCase()
                                        .replace(/[^a-z0-9]/g, "")
                                    }`}
                                    className="border border-black/50 p-2 block"
                                >
                                    <div className="font-['Jersey_10'] uppercase tracking-wide text-[18px] truncate mb-2">
                                        {item.name}
                                    </div>

                                    <div className="border border-black bg-gray-200 w-full h-[120px] flex items-center justify-center">
                                        <img
                                            src={item.smallImg}
                                            alt={item.name}
                                            className="max-w-full max-h-full object-contain"
                                            loading="lazy"
                                        />
                                    </div>

                                    <div className={`grid gap-1 mt-2 ${fontScale > 1.1 ? 'grid-cols-1' : 'grid-cols-3'}`}>
                                        <div className="border border-black bg-gray-200 px-1 py-1 text-center">
                                            <div className="text-[10px] uppercase leading-none">Min</div>
                                            <div className="text-[12px] leading-none">{item.minValue ?? "-"}</div>
                                        </div>

                                        <div className="border border-black bg-gold px-1 py-1 text-center">
                                            <div className="text-[10px] uppercase leading-none">Avg</div>
                                            <div className="text-[12px] font-bold leading-none">{item.avgValue ?? "-"}</div>
                                        </div>

                                        <div className="border border-black bg-gray-200 px-1 py-1 text-center">
                                            <div className="text-[10px] uppercase leading-none">Max</div>
                                            <div className="text-[12px] leading-none">{item.maxValue ?? "-"}</div>
                                        </div>
                                    </div>

                                    <div className="text-[11px] mt-2 opacity-70">
                                        {formatRelativeTime(item.updatedTimestamp)}
                                    </div>
                                </Link>
                            ))}
                        </div>
                        <div className="w-full text-right block underline font-['Jersey_10'] uppercase tracking-wide text-[14px]">
                            <Link to="/values" className="">See More...</Link>
                        </div>
                    </ContentBox>

                    <ContentBox
                        theme="default"
                        tint="green"
                        icon="img/hat.png"
                        header="Latest Guides"
                    >
                        <div className="grid gap-3 grid-cols-1 lg:grid-cols-3">
                            {latestGuides.map((guide) => (
                                <Link
                                    key={guide.id}
                                    to={guide.href}
                                    className="group flex flex-col bg-offwhite border border-black/50 overflow-hidden cursor-pointer"
                                >
                                    <div className="relative overflow-hidden bg-gray-200 h-[120px]">
                                        {guide.headerImage ? (
                                            <img
                                                src={guide.headerImage}
                                                alt={guide.title}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                                style={{ imageRendering: 'auto' }}
                                                onError={(e) => {
                                                    e.currentTarget.style.display = 'none';
                                                    e.currentTarget.nextSibling.style.display = 'flex';
                                                }}
                                            />
                                        ) : null}
                                        <div
                                            className="w-full h-full items-center justify-center bg-gray-200"
                                            style={{ display: guide.headerImage ? 'none' : 'flex' }}
                                        >
                                            <img
                                                src={`${process.env.PUBLIC_URL}/img/${guide.isFranklyGuide ? 'frankly_news.png' : 'frankly_guides.png'}`}
                                                alt=""
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                        <div className="absolute top-2 left-2">
                                            <span
                                                className="font-['Jersey_10'] uppercase text-[11px] tracking-[1px] text-white px-2 py-0.5"
                                                style={{
                                                    backgroundColor: guide.isFranklyGuide ? 'rgba(90,41,41,0.85)' : 'rgba(34,100,34,0.85)',
                                                    boxShadow: '0 2px 0 0 rgba(0,0,0,0.3)',
                                                }}
                                            >
                                                {guide.categoryName}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex flex-col flex-1 p-3 gap-1.5">
                                        <h3
                                            className="font-['Jersey_10'] uppercase text-lg tracking-[1px] text-gray-900 leading-tight group-hover:text-green-800 transition-colors"
                                            style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                                        >
                                            {guide.title}
                                        </h3>
                                        <p
                                            className="text-gray-600 text-xs leading-relaxed flex-1"
                                            style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                                        >
                                            {guide.flavour}
                                        </p>
                                        <div className="flex items-center justify-between pt-2 border-t border-gray-200 mt-auto">
                                            <span className="text-sm text-gray-500 font-['Jersey_10'] uppercase tracking-wide">{guide.author}</span>
                                            <span className="text-sm text-gray-400 font-['Jersey_10'] uppercase tracking-wide">Read more →</span>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                        <div className="w-full text-right block underline font-['Jersey_10'] uppercase tracking-wide text-[14px] mt-2">
                            <Link to="/news/guides">See More...</Link>
                        </div>
                    </ContentBox>

                    <ContentBox theme="default" tint="#ece4da" icon="img/icons/icon_338.png" header="Recommended Sites!">
                        <div className="grid gap-2 [grid-template-columns:repeat(auto-fit,minmax(250px,1fr))]">
                            <a
                                href="https://furniindex.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block"
                            >
                                <div className="cursor-pointer relative border border-black/50 p-2 h-[130px] bg-[#ece4da] flex justify-center items-center font-furniIndex text-[48px] font-normal tracking-normal normal-case text-left">
                                    Furni Index

                                    <HabboImage
                                        habbo="Vincent"
                                        hotel="COM"
                                        action="wav"
                                        head_direction={3}
                                        direction={2}
                                        size="a"
                                        gesture="sml"
                                        className="absolute bottom-2 left-4"
                                        alt=""
                                    />
                                    <HabboImage
                                        habbo="hustle"
                                        hotel="COM"
                                        action="wav"
                                        direction={4}
                                        head_direction={3}
                                        size="a"
                                        className="absolute bottom-2 right-4"
                                        alt=""
                                    />
                                </div>
                            </a>

                            <a
                                href="https://habbogardening.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block"
                            >
                                <div className="cursor-pointer relative border border-black/50 overflow-hidden h-[130px] bg-[url('https://habbogardening.com/assets/layout/16.gif')] bg-repeat">
                                    <div className="bg-[#F8F9FA] flex justify-center items-center  p-4 w-full shadow-[0_2px_3px_rgba(6,19,25,0.7)] mt-2">
                                        <img
                                            src="https://habbogardening.com/assets/layout/habbogardening.png"
                                            alt=""
                                        />
                                    </div>

                                    <HabboImage
                                        habbo="ICE..Skater"
                                        hotel="COM"
                                        action="wav"
                                        direction={4}
                                        head_direction={3}
                                        size="b"
                                        className="absolute bottom-[-40px] right-7 z-20"
                                        alt=""
                                    />
                                    <img
                                        src="https://furni.franklyorigins.net/api/furniture/render?furni=plant_yukka&direction=0&state=0&frame=0&width=120&height=120&rt=0"
                                        className="absolute bottom-[-70px] right-[-10px] z-10"
                                        alt=""
                                    />
                                    <img
                                        src="https://furni.franklyorigins.net/api/furniture/render?furni=plant_yukka&direction=0&state=0&frame=0&width=120&height=120&rt=0"
                                        className="absolute bottom-[-85px] left-[-30px] z-10"
                                        alt=""
                                    />
                                </div>
                            </a>

                            <a
                                href="https://habbofishing.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block"
                            >
                                <div className="cursor-pointer relative border border-black/50 overflow-hidden h-[130px] bg-[url('https://habbofishing.com/assets/layout/bg_water.gif')] bg-repeat">
                                    <div className="bg-[#F8F9FA] flex justify-center items-center absolute bottom-2 p-4 w-full shadow-[0_2px_3px_rgba(6,19,25,0.7)] z-30">
                                        <img
                                            src="https://habbofishing.com/assets/layout/habbofishing.png"
                                            alt=""
                                        />
                                    </div>

                                    <HabboImage
                                        habbo="ice..skater"
                                        hotel="COM"
                                        head_direction={3}
                                        size="a"
                                        gesture="sml"
                                        crr={11}
                                        className="absolute top-[20px] left-8 z-10"
                                        alt=""
                                    />
                                    <img
                                        src="https://furni.franklyorigins.net/api/furniture/render?furni=easel_4&direction=1&state=0&frame=0&width=120&height=120&rt=0"
                                        className="absolute top-[-20px] right-6 z-10"
                                        alt=""
                                    />
                                </div>
                            </a>

                            <a
                                href="https://bobba.me"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block"
                            >
                                <div className="cursor-pointer relative border border-black/50 p-2 h-[130px] overflow-hidden bg-[#21130d] flex justify-center text-white items-center font-['Jersey_10'] text-[48px] font-normal tracking-normal normal-case text-left bg-[url('https://bobba.me/assets/images/bg/bobba.png')] bg-repeat">
                                    Bobba.me


                                    <HabboImage
                                        habbo="icing"
                                        hotel="COM"
                                        action="wav"
                                        direction={2}
                                        head_direction={3}
                                        gesture={"sml"}
                                        size="b"
                                        className="absolute bottom-[-40px] left-5 z-20"
                                        alt=""
                                    />

                                    <img
                                        src="https://furni.franklyorigins.net/api/furniture/render?furni=doormat_bobba&direction=7&state=1&frame=0&width=120&height=120"
                                        className="absolute top-[90px] right-6 z-10"
                                        alt=""
                                    />

                                </div>
                            </a>

                            <a href="https://rockhabbo.com"
                               target="_blank"
                               rel="noopener noreferrer"
                               className="block"
                            >
                                <div className="cursor-pointer relative border border-black/50 p-2 h-[130px] overflow-hidden bg-[#1a0a0a] flex justify-center text-white items-center font-['Jersey_10'] text-[48px] font-normal tracking-normal normal-case text-left bg-[url('https://rockhabbo.com/images/header-bg.png')] bg-repeat">
                                    Rockhabbo

                                    <HabboImage
                                        habbo="Upthevale"
                                        hotel="COM"
                                        action="wav"
                                        direction={2}
                                        head_direction={3}
                                        gesture="sml"
                                        size="b"
                                        className="absolute bottom-[-40px] left-5 z-20"
                                        alt=""
                                    />

                                    <img
                                        src="https://furni.franklyorigins.net/api/furniture/render?furni=fx_rockon&direction=0&state=0&frame=0&width=120&height=120"
                                        className="absolute top-[60px] right-6 z-10"
                                        alt=""
                                    />
                                </div>
                            </a>


                            <a
                                href="https://duckieworld.com/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block"
                            >
                                <div
                                    className="cursor-pointer relative border border-black/50 p-2 h-[130px] overflow-hidden
        bg-[url('https://api.franklyorigins.net/uploads/1772478320057-Banner2.png')]
        bg-cover bg-center bg-no-repeat
        flex justify-center text-white items-center
        font-['Jersey_10'] text-[48px] font-normal tracking-normal normal-case text-left"
                                >
                                    <HabboImage
                                        habbo="Dovak"
                                        hotel="COM"
                                        action="wav"
                                        direction={4}
                                        head_direction={3}
                                        gesture="sml"
                                        size="b"
                                        className="absolute bottom-[-40px] right-5 z-20"
                                        alt=""
                                    />

                                    <HabboImage
                                        habbo="Spidercides"
                                        hotel="COM"
                                        action="wav"
                                        direction={2}
                                        head_direction={3}
                                        gesture="sml"
                                        size="b"
                                        className="absolute bottom-[-40px] left-5 z-20"
                                        alt=""
                                    />

                                    <img
                                        src="https://furni.franklyorigins.net/api/furniture/render?furni=computer2&direction=2&state=0&frame=0&width=120&height=120"
                                        className="absolute top-[50px] left-4 z-10"
                                        alt=""
                                    />
                                </div>
                            </a>

                            <a
                                href="https://solochef.io"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block"
                            >
                                <div
                                    className="cursor-pointer relative border border-black/50 p-2 h-[130px] overflow-hidden
        bg-[url('https://solochef.io/assets/image_1770359097195-CzLxagOD.png')]
        bg-center bg-no-repeat
        flex justify-center text-white items-center
        font-['Jersey_10'] text-[48px] font-normal tracking-normal normal-case text-left"
                                >
                                    <HabboImage
                                        habbo="BobBarker"
                                        hotel="COM"
                                        action="wav"
                                        direction={2}
                                        head_direction={3}
                                        gesture="sml"
                                        size="b"
                                        className="absolute bottom-[-40px] left-[120px] z-20"
                                        alt=""
                                    />

                                    <img
                                        src="/img/solochef.png"
                                        className="absolute top-[-30px] left-4 z-10"
                                        alt=""
                                    />
                                </div>
                            </a>

                            <a
                                href="https://habbo.dek.cx/app/index.html"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block"
                            >
                                <div
                                    className="cursor-pointer relative border border-black/50 p-2 h-[130px] overflow-hidden
        bg-[#0d1117]
        bg-center bg-no-repeat
        flex justify-center text-white items-center
        font-['Jersey_10'] text-[48px] font-normal tracking-normal normal-case text-left"
                                >
                                    <HabboImage
                                        habbo="Dek"
                                        hotel="COM"
                                        action="wav"
                                        direction={4}
                                        head_direction={3}
                                        gesture="sml"
                                        size="b"
                                        className="absolute bottom-[-40px] right-[0px] z-20"
                                        alt=""
                                    />

                                    <img
                                        src="/img/habboarchive.png"
                                        className="absolute top-[14px] left-[12px] z-10 scale-[2] origin-top-left"
                                        style={{ imageRendering: 'pixelated' }}
                                        alt=""
                                    />
                                </div>
                            </a>

                        </div>
                    </ContentBox>
                </div>

                <div className="min-w-0">

                    <ContentBox theme="poll" icon="img/icons/icon_164.png">
                        <PollBox userData={userData} />
                    </ContentBox>

                    <ContentBox theme="default" tint="maroon" header="Latest Articles" padding="p-0" icon="img/icons/icon_264.png">
                        <LatestArticles />
                    </ContentBox>

                    <ContentBox theme="default" header="">
                        <div className="flex flex-col items-center">
                            <a
                                href="/observer"
                                rel="noopener noreferrer"
                                className="block"
                            >
                                <img
                                    src={`${process.env.PUBLIC_URL}/img/observerhome.png`}
                                    alt=""
                                    className="block w-[328px] h-[120px] max-w-none shrink-0 outline outline-1 outline-black"
                                    width={328}
                                    height={120}
                                    style={{ imageRendering: 'pixelated' }}
                                />
                            </a>
                            <a
                                href="/observer"
                                rel="noopener noreferrer"
                                className="block w-[330px] max-w-none shrink-0 text-center text-white hover:opacity-90 px-6 py-2 font-['Jersey_10'] uppercase text-[17px] tracking-[1px] transition-opacity"
                                style={{ boxShadow: '0 3px 0 0 rgba(0, 0, 0, 0.3)', backgroundColor: '#488fa3' }}
                            >
                                Read the latest issue!
                            </a>
                        </div>
                    </ContentBox>

                    <ContentBox theme="outlines" tint="#45ad00" header="Habbo Profile Search" icon="img/icons/icon_297.png">
                        <ProfileSearch />
                    </ContentBox>

                    <ContentBox theme="default" header="">
                        <div className="flex flex-col items-center">
                            <a
                                href="https://discord.gg/vehU36G44N"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block"
                            >
                                <img
                                    src={`${process.env.PUBLIC_URL}/img/discordhome.png`}
                                    alt="Join the Frankly Origins Discord"
                                    className="block w-[328px] h-[120px] max-w-none shrink-0 outline outline-1 outline-black"
                                    width={328}
                                    height={120}
                                    style={{ imageRendering: 'pixelated' }}
                                />
                            </a>
                            <a
                                href="https://discord.gg/vehU36G44N"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block w-[330px] max-w-none shrink-0 text-center text-white hover:opacity-90 px-6 py-2 font-['Jersey_10'] uppercase text-[17px] tracking-[1px] transition-opacity"
                                style={{ boxShadow: '0 3px 0 0 rgba(0, 0, 0, 0.3)', backgroundColor: '#5865F2' }}
                            >
                                Join our Discord!
                            </a>
                        </div>
                    </ContentBox>

                    <div name="StampCardContainer" className="flex items-center mb-5 justify-center w-full">
                        <div style={{ zoom: 1 / fontScale }}>
                            <StampCard
                                userData={userData}
                                stamps={stamps.totalStamps}
                                completedCards={stamps.completedCards}
                                loading={loadingStamps}
                            />
                        </div>
                    </div>

                    <ContentBox
                        theme="default"
                        tint="gold"
                        icon="img/trophygold.png"
                        header=""
                    >
                        <p className="text-gray-700">
                            <b className="font-['Jersey_10'] uppercase tracking-wide text-lg">
                                Want to join our team?
                            </b><br />
                            Contact a current team member and let us know what you'd like to be involved in!
                            <br />
                            <span className="text-xs">
                                Please note - some teams may already be full, please do not be disheartened if we can't fit you in!
                            </span>
                        </p>
                    </ContentBox>
                </div>
            </div>
        </div>
    );
}

export default Home;